import {DICTIONARY} from '../dictionary';
import {GeneralService} from "./generalService";
import {Injectable} from "@angular/core";
import _ from "lodash";


@Injectable({
	providedIn: 'root'
})
export class InboundTrendsService {
	dic = DICTIONARY;

	TP_METRICS: any = Object.values(this.dic.CONSTANTS.threatProtection.metrics);

	constructor(public gs:GeneralService) {}

	parseTrendsData(userInfo, emails, currentQuery) {
		let trendsPeriod: any = this.getTrendsPeriodForGraphs(currentQuery);

		const trendsData = {
			quarantined: {
				labels: [],
				data: [[]],
				series: ["Quarantined"],
			},
			general: {
				malicious: 0,
				suspicious: 0,
				spam: 0,
				graymail: 0,
				blocklist: 0,
				external: 0
			},
			detectionType: {
				labels: [],
				data: []
			},
			detectionTypeBars: {
				labels: [],
				data: [],
				series: []
			},
			domain: {
				labels: [],
				data: []
			},
			mailbox: {
				labels: [],
				data: [[], [], []],
				series: ['Malicious', 'Suspicious', 'Spam']
			},
			executiveMailboxes:{
				labels: [],
				data: [[], [], []],
				series: ['Malicious', 'Suspicious', 'Spam']
			},
			inboundStats: {
				total: 0,
				labels: [],
				data: [[], [], [], []],
				series: ['Malicious', 'Suspicious', 'Spam', 'Graymail'],
			}
		};

		// @ts-ignore
		emails.sort((a, b) => new Date(a.created) - new Date(b.created));

		const generalEmailSource = {
			detectionType: {},
			domain: {},
			mailbox: {},
			executiveMailboxes: {}
		};

		let emailSourcePerPeriod = {quarantined: {}, dateStart: false, dateEnd: undefined};
		const skipCategories = ['Blocklist', 'Allowlist', 'User Preferences'];

		emails.forEach((currentSource) => {
			const tpStatus = (currentSource.malicious.status_user_reported || currentSource.malicious.status).toLowerCase();
			currentSource.malicious.status = tpStatus;
			currentSource.malicious.tpResults.status = tpStatus; // TODO: remove

			if (currentSource.malicious.tpResults.resHead?.category && skipCategories.includes(currentSource.malicious.tpResults.resHead?.category) ||
				currentSource.malicious.tpResults.resHash?.category && skipCategories.includes(currentSource.malicious.tpResults.resHash?.category) ||
				currentSource.malicious.tpResults.resUrl?.category && skipCategories.includes(currentSource.malicious.tpResults.resUrl?.category)) {
				if (currentSource.malicious.tpResults.resHead?.category === 'Blocklist' ||
					currentSource.malicious.tpResults.resHash?.category === 'Blocklist' ||
					currentSource.malicious.tpResults.resUrl?.category === 'Blocklist') {
					trendsData.general.blocklist++;
				}
				return;
			}

			if (this.shouldSkipEmailForTrends(currentSource)) {
				return;
			}

			this.addGeneralCategory(currentSource.malicious, trendsData);

			trendsData.inboundStats.total += 1;

			if (!emailSourcePerPeriod.dateStart) {
				emailSourcePerPeriod = this.initEmailSourcePerPeriod(currentSource, trendsPeriod);
				emailSourcePerPeriod.quarantined = {
					stillQuarantined: 0
				};
			}

			this.getTrendsGeneralResult(userInfo, currentSource, currentSource.malicious.tpResults, generalEmailSource, emailSourcePerPeriod);

			if (!this.gs.isSamePeriod(trendsPeriod, currentSource.created, emailSourcePerPeriod.dateEnd)) {
				this.addThreatStatusData(trendsData, emailSourcePerPeriod, trendsPeriod);
				this.addThreatsCategoryData(trendsData, emailSourcePerPeriod, trendsPeriod);
				this.getTrendsDetectionTypeBars(trendsData, emailSourcePerPeriod, trendsPeriod);

				emailSourcePerPeriod = this.initEmailSourcePerPeriod(currentSource, trendsPeriod);
				emailSourcePerPeriod.quarantined = {
					stillQuarantined: 0
				};
			}

			this.getTrendsPeriodResults(currentSource, emailSourcePerPeriod);
			this.getQuarantinedEmailAdminResult(currentSource.malicious, emailSourcePerPeriod);
		});

		this.addThreatStatusData(trendsData, emailSourcePerPeriod, trendsPeriod);
		this.addThreatsCategoryData(trendsData, emailSourcePerPeriod, trendsPeriod);
		this.getTrendsDetectionTypeBars(trendsData, emailSourcePerPeriod, trendsPeriod);
		this.normalizeTrendsDetectionTypeBars(trendsData);

		this.addGeneralTrendsGraphData(trendsData, generalEmailSource);

		this.sortMailboxes(trendsData);
		this.sortExecutiveMailboxes(trendsData);
		this.sortDomains(trendsData);

		return trendsData;
	}

	shouldSkipEmailForTrends(currentSource) {
		if (currentSource.malicious.status === 'External') {
			return true;
		}

		// do not count manually released emails (but do count journal (monitor) mode))
		if (currentSource.status === this.dic.CONSTANTS.tpRule.status.released &&
			currentSource.reason !== this.dic.CONSTANTS.releaseReason.rule && currentSource.reason !== 'Monitor mode' &&
			currentSource.reason !== this.dic.CONSTANTS.releaseReason.whitelistedByAdmin &&
			currentSource.reason !== this.dic.CONSTANTS.releaseReason.folderRule) {
			return true;
		}

		// do not count simulation emails in malicious statistics
		if (currentSource.malicious.status === this.dic.CONSTANTS.tpRule.name.malicious &&
			this.hasSimulationInSubStatus(currentSource.malicious.tpResults)) {
			return true;
		}
	}

	hasSimulationInSubStatus(tpResults) {
		try {
			const sections = ['resHead', 'resUrl', 'resHash'];

			for (const section of sections) {
				const sectionData = tpResults?.[section];
				if (!sectionData || !sectionData.sub_status) {
					continue;
				}

				const subStatusArray = Array.isArray(sectionData.sub_status)
					? sectionData.sub_status
					: [sectionData.sub_status];

				for (const subStatus of subStatusArray) {
					let subStatusText = '';
					if (typeof subStatus === 'string') {
						subStatusText = subStatus;
					}
					else if (typeof subStatus === 'object' && subStatus) {
						subStatusText = subStatus.sub_status || subStatus.message || subStatus.summary || '';
					}

					if (subStatusText && typeof subStatusText === 'string' && subStatusText.includes('Simulation')) {
						return true;
					}
				}
			}
			return false;
		}
		catch (e) {
			return false;
		}
	}

	getTrendsPeriodForGraphs(currentQuery) {
		let trendsPeriod = currentQuery.period;
		if (currentQuery.period === this.dic.CONSTANTS.trendsPeriod.range.value && currentQuery.range) {
			const days = this.gs.dateDiffInDays(currentQuery.range.end, currentQuery.range.start);
			//35 = 7 days * 5 weeks - limit graph to 5 weeks
			if (days >= 90) {
				trendsPeriod = this.dic.CONSTANTS.trendsPeriod.lastMonths.value;
			}
			else if (days >= 7) {
				trendsPeriod = this.dic.CONSTANTS.trendsPeriod.lastWeek.value;
			}
			else {
				trendsPeriod = this.dic.CONSTANTS.trendsPeriod.lastDay.value;
			}
		}
		return trendsPeriod;
	}

	initEmailSourcePerPeriod(currentSource, period) {
		const emailSourcePerPeriod = this.gs.calcDatesForPeriod(currentSource, period);
		emailSourcePerPeriod.malicious = 0;
		emailSourcePerPeriod.suspicious = 0;
		emailSourcePerPeriod.spam = 0;
		emailSourcePerPeriod.graymail = 0;

		emailSourcePerPeriod.detectionType = {};

		return emailSourcePerPeriod;
	}

	getQuarantinedEmailAdminResult(maliciousObj, emailSourcePerPeriod) {
		switch (maliciousObj.status) {
			case this.dic.CONSTANTS.tpRule.name.malicious:
				emailSourcePerPeriod.malicious++;
				break;

			case this.dic.CONSTANTS.tpRule.name.suspicious:
				emailSourcePerPeriod.suspicious++;
				break;

			case this.dic.CONSTANTS.tpRule.name.spam:
				emailSourcePerPeriod.spam++;
				break;

			case this.dic.CONSTANTS.tpRule.name.graymail:
				emailSourcePerPeriod.graymail++;
				break;
		}
	}

	sortMailboxes(trendsData) {
		if (!trendsData.mailbox?.labels.length) {
			return;
		}

		let mailboxesAttackMap = [];
		for (let i = 0; i < trendsData.mailbox.labels.length; i++) {
			mailboxesAttackMap[i] = {mailbox: trendsData.mailbox.labels[i], counter: 0, index: i};
			for (let j = 0; j < trendsData.mailbox.series.length; j++) {
				mailboxesAttackMap[i].counter += trendsData.mailbox.data[j][i];
			}
		}

		mailboxesAttackMap = mailboxesAttackMap.sort((a, b) => b.counter - a.counter);
		let sortMailboxesInfo = {data: [[],[],[]], labels: []};
		// return top 100 mailboxes
		const numberOfMailboxes = Math.min(trendsData.mailbox.labels.length, 100);
		for (let i = 0; i < numberOfMailboxes; i++) {
			sortMailboxesInfo.labels.push(mailboxesAttackMap[i].mailbox);
			sortMailboxesInfo.data[0].push(trendsData.mailbox.data[0][mailboxesAttackMap[i].index]);
			sortMailboxesInfo.data[1].push(trendsData.mailbox.data[1][mailboxesAttackMap[i].index]);
			sortMailboxesInfo.data[2].push(trendsData.mailbox.data[2][mailboxesAttackMap[i].index]);
		}
		trendsData.mailbox.data = sortMailboxesInfo.data;
		trendsData.mailbox.labels = sortMailboxesInfo.labels;
	}

	sortExecutiveMailboxes(trendsData) {
		if (!trendsData.executiveMailboxes.labels.length) {
			return;
		}

		let mailboxesAttackMap = [];
		for (let i = 0; i < trendsData.executiveMailboxes.labels.length; i++) {
			mailboxesAttackMap[i] = {mailbox: trendsData.executiveMailboxes.labels[i], counter: 0, index: i};
			for (let j = 0; j < trendsData.executiveMailboxes.series.length; j++) {
				mailboxesAttackMap[i].counter += trendsData.executiveMailboxes.data[j][i];
			}
		}

		mailboxesAttackMap = mailboxesAttackMap.sort((a, b) => b.counter - a.counter);
		let sortMailboxes = {data: [[],[],[]], labels: []};
		// return top 100 mailboxes
		const numberOfMailboxes = Math.min(trendsData.executiveMailboxes.labels.length, 100);
		for (let i = 0; i < numberOfMailboxes; i++) {
			sortMailboxes.labels.push(mailboxesAttackMap[i].mailbox);
			sortMailboxes.data[0].push(trendsData.executiveMailboxes.data[0][mailboxesAttackMap[i].index]);
			sortMailboxes.data[1].push(trendsData.executiveMailboxes.data[1][mailboxesAttackMap[i].index]);
			sortMailboxes.data[2].push(trendsData.executiveMailboxes.data[2][mailboxesAttackMap[i].index]);
		}
		trendsData.executiveMailboxes.data = sortMailboxes.data;
		trendsData.executiveMailboxes.labels = sortMailboxes.labels;
	}


	sortDomains(trendsData) {
		if (!trendsData.domain.labels.length) {
			return;
		}

		let domainsAttackMap = [];
		for (let i = 0; i < trendsData.domain.labels.length; i++) {
			domainsAttackMap[i] = {domain: trendsData.domain.labels[i], counter: trendsData.domain.data[i], index: i};
		}

		domainsAttackMap = domainsAttackMap.sort((a, b) => b.counter - a.counter);
		const sortDomains = {data: [], labels: []};
		for (let i = 0; i < trendsData.domain.labels.length; i++) {
			sortDomains.labels.push(domainsAttackMap[i].domain);
			sortDomains.data.push(trendsData.domain.data[domainsAttackMap[i].index]);
		}

		trendsData.domain.data = sortDomains.data;
		trendsData.domain.labels = sortDomains.labels;
	}

	addGeneralTrendsGraphData(trendsData, generalEmailSource) {

		generalEmailSource.detectionType = this.gs.sortObjectByValue(generalEmailSource.detectionType, true);
		this.addParsedResult(trendsData.detectionType, generalEmailSource.detectionType);

		// this.markBlacklistDomains(generalEmailSource.domain);
		generalEmailSource.domain = this.gs.sortObjectByValue(generalEmailSource.domain, true);
		this.addParsedResult(trendsData.domain, generalEmailSource.domain);

		let totalThreatsPerMailbox = _.mapValues(generalEmailSource.mailbox, m => m.malicious + m.suspicious + m.spam);
		totalThreatsPerMailbox = this.gs.sortObjectByValue(totalThreatsPerMailbox, true);
		Object.keys(totalThreatsPerMailbox).forEach(mailbox => {
			totalThreatsPerMailbox[mailbox] = generalEmailSource.mailbox[mailbox];
		});
		this.addMailboxResult(trendsData.mailbox, totalThreatsPerMailbox);

		let totalThreatsPerExecutiveMailbox = _.mapValues(generalEmailSource.executiveMailboxes, m => m.malicious + m.suspicious + m.spam);
		totalThreatsPerExecutiveMailbox = this.gs.sortObjectByValue(totalThreatsPerExecutiveMailbox, true);
		Object.keys(totalThreatsPerExecutiveMailbox).forEach(mailbox => {
			totalThreatsPerExecutiveMailbox[mailbox] = generalEmailSource.executiveMailboxes[mailbox];
		});
		this.addMailboxResult(trendsData.executiveMailboxes, totalThreatsPerExecutiveMailbox);
	}

	addThreatStatusData(trendsData, emailSourcePerPeriod, period) {
		if (emailSourcePerPeriod.dateStart && emailSourcePerPeriod.quarantined) {
			const dateLabel = this.gs.getPeriodLabel(period, emailSourcePerPeriod);

			trendsData.quarantined.labels.push(dateLabel);
			trendsData.quarantined.data[0].push(emailSourcePerPeriod.quarantined.stillQuarantined);
		}
	}

	addThreatsCategoryData(inboundData, emailSourcePerPeriod, period) {
		if (emailSourcePerPeriod.dateStart && (emailSourcePerPeriod.malicious || emailSourcePerPeriod.suspicious || emailSourcePerPeriod.spam || emailSourcePerPeriod.graymail)) {
			const dateLabel = this.gs.getPeriodLabel(period, emailSourcePerPeriod);

			inboundData.inboundStats.labels.push(dateLabel);
			inboundData.inboundStats.data[0].push(emailSourcePerPeriod.malicious);
			inboundData.inboundStats.data[1].push(emailSourcePerPeriod.suspicious);
			inboundData.inboundStats.data[2].push(emailSourcePerPeriod.spam);
			inboundData.inboundStats.data[3].push(emailSourcePerPeriod.graymail);
		}
	}

	addParsedResult(data, resultObj) {
		let keys = Object.keys(resultObj);
		if (keys.length > 0) {
			for (let i = 0; i < keys.length; i++) {
				data.labels.push(keys[i]);
				data.data.push(resultObj[keys[i]]);
			}
		}
	}

	addMailboxResult(data, mailboxesObj) {
		let mailboxesNames = Object.keys(mailboxesObj);
		if (mailboxesNames.length > 0) {
			mailboxesNames = mailboxesNames.filter((k) => {
				return k !== '';
			});

			for (let i = 0; i < mailboxesNames.length; i++) {
				if (mailboxesObj[mailboxesNames[i]].malicious || mailboxesObj[mailboxesNames[i]].suspicious || mailboxesObj[mailboxesNames[i]].spam) {
					data.labels.push(mailboxesNames[i]);
					data.data[0].push(mailboxesObj[mailboxesNames[i]].malicious);
					data.data[1].push(mailboxesObj[mailboxesNames[i]].suspicious);
					data.data[2].push(mailboxesObj[mailboxesNames[i]].spam);
				}
			}
		}
	}

	addGeneralCategory(maliciousObj, trendsData) {
		switch (maliciousObj.status) {
			case this.dic.CONSTANTS.tpRule.name.malicious:
				trendsData.general.malicious++;
				break;

			case this.dic.CONSTANTS.tpRule.name.suspicious:
				trendsData.general.suspicious++;
				break;

			case this.dic.CONSTANTS.tpRule.name.spam:
				trendsData.general.spam++;
				break;

			case this.dic.CONSTANTS.tpRule.name.graymail:
				trendsData.general.graymail++;
				break;
		}
	}

	getTrendsGeneralResult(userInfo, currentSource, tpResults, generalEmailSource, emailSourcePerPeriod) {

		if (tpResults.status === this.dic.CONSTANTS.tpRule.name.malicious ||
			tpResults.status === this.dic.CONSTANTS.tpRule.name.spam) {
			const theSpfDomain = currentSource.malicious?.tpResults?.resHead?.from?.receivedSPF?.domain ||
				currentSource.malicious?.tpResults?.resHead?.from?.sendingMTAFull ||
				currentSource.malicious?.tpResults?.resHead?.from?.sendingMTA?.domain;

			if (theSpfDomain && !currentSource.malicious.tpResults.resHead.isTopDomain && !currentSource.malicious.tpResults.resHead.isCertainContact &&
				theSpfDomain !== this.gs.getEmailDomain(userInfo.email) &&
				!['gmail.com', 'google.com', 'yahoo.com', 'outlook.com', 'amazonses.com', 'hotmail.com', 'salesforce.com'].includes(theSpfDomain)) {
				this.addToTrendsCount(generalEmailSource.domain, theSpfDomain);
			}
		}

		if (currentSource.mailbox_email) {
			this.addMailboxToTrendsCount(tpResults, generalEmailSource.mailbox, currentSource.mailbox_email);
			if (currentSource.malicious.tpResults.resHead?.isExecutiveRecipient) {
				this.addMailboxToTrendsCount(tpResults, generalEmailSource.executiveMailboxes, currentSource.mailbox_email);
			}
		}

		this.getTrendsDetectionType(tpResults, generalEmailSource, emailSourcePerPeriod);
	}

	getTrendsDetectionType(tpResults, generalEmailSource, emailSourcePerPeriod) {
		if (tpResults.status !== this.dic.CONSTANTS.tpRule.name.malicious) {
			return;
		}

		let allEmailSubStatus = [];

		if (tpResults.resHead?.status === this.dic.CONSTANTS.threatProtection.status.malicious && tpResults.resHead.sub_status) {
			if (tpResults.resHead.sub_status instanceof Array && tpResults.resHead.sub_status.length) {
				allEmailSubStatus = allEmailSubStatus.concat(tpResults.resHead.sub_status);
			}
			else {
				allEmailSubStatus.push(tpResults.resHead.sub_status);
			}
		}
		else if (tpResults.resUrl?.status === this.dic.CONSTANTS.threatProtection.status.malicious && tpResults.resUrl?.sub_status) {
			if (tpResults.resUrl.sub_status instanceof Array && tpResults.resUrl.sub_status.length) {
				allEmailSubStatus = allEmailSubStatus.concat(tpResults.resUrl.sub_status);
			}
			else {
				allEmailSubStatus.push(tpResults.resUrl.sub_status);
			}
		}
		else if (tpResults.resHash?.status === this.dic.CONSTANTS.threatProtection.status.malicious && tpResults.resHash?.sub_status) {
			if (tpResults.resHash.sub_status instanceof Array && tpResults.resHash.sub_status.length) {
				allEmailSubStatus = allEmailSubStatus.concat(tpResults.resHash.sub_status);
			}
			else {
				allEmailSubStatus.push(tpResults.resHash.sub_status);
			}
		}

		const emailSubStatusCategories = [];
		allEmailSubStatus.forEach((subStatus) => {
			const metricObj = this.TP_METRICS.find(itm => itm.sub_status === subStatus);
			if (!metricObj?.category || metricObj.status !==  this.dic.CONSTANTS.threatProtection.status.malicious) {
				return;
			}
			if (!emailSubStatusCategories.includes(metricObj.category)) {
				emailSubStatusCategories.push(metricObj.category);
				if (!generalEmailSource.detectionType[metricObj.category]) {
					generalEmailSource.detectionType[metricObj.category] = 0;
				}
				generalEmailSource.detectionType[metricObj.category]++;

				// detectionTypeBars
				if (!emailSourcePerPeriod.detectionType[metricObj.category]) {
					emailSourcePerPeriod.detectionType[metricObj.category] = 0;
				}
				emailSourcePerPeriod.detectionType[metricObj.category]++;
			}
		});
	}

	getTrendsDetectionTypeBars(inboundData, emailSourcePerPeriod, period) {
		if (emailSourcePerPeriod.dateStart && !_.isEmpty(emailSourcePerPeriod.detectionType)) {
			const dateLabel = this.gs.getPeriodLabel(period, emailSourcePerPeriod);

			// date
			inboundData.detectionTypeBars.labels.push(dateLabel);
			// category names
			inboundData.detectionTypeBars.series.push(_.keys(emailSourcePerPeriod.detectionType));
			// no. of emails per category
			inboundData.detectionTypeBars.data.push(_.values(emailSourcePerPeriod.detectionType));
		}
	}

	// prepare the data for chart library
	normalizeTrendsDetectionTypeBars = (inboundData) => {

		// current object properties format (inboundData.detectionTypeBars):
		// 		labels: ['date1', 'date2'...]
		// 		series: [['category1', 'category2'], ['category1', 'category3', 'category4']...]
		// 		data: [[25, 26], [1, 2, 18]...]
		////
		// desired format:
		// 		labels: ['date1', 'date2'...]
		// 		series: [{
		// 					name: 'category1', data: [all values corresponding to 'category1' per date]
		// 				}...]

		// gather all detection types (categories) names found
		const allCategoryNames = _.uniq(_.flatten(inboundData.detectionTypeBars.series));

		const series = _.map(allCategoryNames, categoryName => ({
			name: categoryName,
			data: _.map(inboundData.detectionTypeBars.series, (categories, i) => {
				const idx = categories.indexOf(categoryName);
				return idx === -1 ? null : inboundData.detectionTypeBars.data[i][idx];
			})
		}));

		inboundData.detectionTypeBars = {
			labels: inboundData.detectionTypeBars.labels,
			series
		};
	}


	getTrendsPeriodResults(currentSource, emailSourcePerPeriod) {
		if (currentSource.status === this.dic.CONSTANTS.tpRule.status.quarantined) {
			emailSourcePerPeriod.quarantined.stillQuarantined++;
		}
	}

	addMailboxToTrendsCount(tpResults, mailboxesEmailsStats, mailboxEmail) {
		if (!mailboxesEmailsStats[mailboxEmail]) {
			mailboxesEmailsStats[mailboxEmail] = {
				malicious: 0,
				suspicious: 0,
				spam: 0,
				graymail: 0
			};
		}

		switch (tpResults.status) {
			case this.dic.CONSTANTS.tpRule.name.malicious:
				mailboxesEmailsStats[mailboxEmail].malicious++;
				break;

			case this.dic.CONSTANTS.tpRule.name.suspicious:
				mailboxesEmailsStats[mailboxEmail].suspicious++;
				break;

			case this.dic.CONSTANTS.tpRule.name.spam:
				mailboxesEmailsStats[mailboxEmail].spam++;
				break;

			case this.dic.CONSTANTS.tpRule.name.graymail:
				mailboxesEmailsStats[mailboxEmail].graymail++;
				break;
		}
	}

	addToTrendsCount(emailsStats, key) {
		if (!key) return;
		if (emailsStats[key]) {
			emailsStats[key]++;
		}
		else {
			emailsStats[key] = 1;
		}
	}

}
